import { AppShell, Box, Breadcrumbs, Flex, Group, Input, Switch, useMantineTheme } from '@mantine/core';
import { IconExclamationCircle, IconSearch } from '@tabler/icons-react';
import H2Medium from 'components/typography/H2Medium';
import MobileBottomNavigation from 'layouts/dashboard/MobileBottomNavigation';
import Navbar from 'layouts/dashboard/navbar/Navbar';
import NavbarImpersonating from 'layouts/dashboard/navbar/NavbarImpersonating';
import NavbarPerson from 'layouts/dashboard/navbar/NavbarPerson';
import { noop } from 'lodash';
import { useEffect, useMemo } from 'react';
import { DASHBOARD_PAGE_PATH } from 'routes/paths';
import { Breadcrumb } from 'layouts/dashboard/Breadcrumb';
import P1Medium from 'components/typography/P1Medium';
import { HTML_TITLE } from 'env';
import { DashboardLayoutProvider, useDashboardLayout } from 'layouts/dashboard/use-dashboard-layout';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * Parameters of the DashboardLayout.
 */
export interface DashboardLayoutProps {
  children: React.ReactNode | React.ReactNode[];
  title?: string;
  titleColor?: string;
  isDiscarded?: boolean;
  breadcrumbs?: { title: string; link?: string }[];
  height?: 'auto' | 'screen' | '100%';
  withSearch?: boolean;
  withToggle?: boolean;
  toggleLabel?: string;
  actions?: React.ReactNode;
  hideBreadcrumbs?: boolean;
  groupPosition?: 'apart' | 'left';
  onSearch?: (value: string) => void;
  onToggleChange?: (value: boolean) => void;
}

/**
 * Dashboard layout.
 */
function DashboardLayoutImpl({
  children,
  title = '',
  titleColor = '',
  isDiscarded = false,
  breadcrumbs = [],
  height = 'auto',
  withSearch = false,
  withToggle = false,
  toggleLabel = '',
  actions = null,
  hideBreadcrumbs = false,
  groupPosition = 'apart',
  onSearch = noop,
  onToggleChange = noop,
}: DashboardLayoutProps) {
  const theme = useMantineTheme();
  const { isMobile, isTablet } = useResponsiveDesign();
  const { padding, titleHeight, breadcrumbsHeight, footerHeight, setMobileHeaderActionsTarget } = useDashboardLayout();
  const showTitleRow = !isMobile && title !== '';
  const reservedHeight = padding * 2 + breadcrumbsHeight + footerHeight + (showTitleRow ? titleHeight : 0);

  const h = useMemo(() => {
    if (height === 'screen') {
      return isMobile
        ? `calc(100vh - ${reservedHeight}px - env(safe-area-inset-bottom))`
        : `calc(100vh - ${reservedHeight}px)`;
    }

    return height;
  }, [height, isMobile, reservedHeight]);

  useEffect(() => {
    const titles = breadcrumbs.map(({ title }) => title);
    document.title = [HTML_TITLE, ...titles].join(' - ');
  }, [breadcrumbs]);

  const mobileTopActions = isMobile ? (
    <Group spacing={8} noWrap>
      {actions}
      <Box ref={setMobileHeaderActionsTarget} sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }} />
      <NavbarImpersonating isCollapsed />
      <NavbarPerson isCollapsed />
    </Group>
  ) : null;

  const breadcrumbItems = isMobile
    ? breadcrumbs
    : [{ title: 'OBJEKTO', link: DASHBOARD_PAGE_PATH.original }, ...breadcrumbs];

  return (
    <AppShell
      padding={padding}
      navbar={isMobile ? undefined : <Navbar />}
      footer={isMobile ? <MobileBottomNavigation /> : undefined}
      styles={{
        main: {
          paddingBottom: isMobile ? `calc(${padding}px + ${footerHeight}px + env(safe-area-inset-bottom))` : undefined,
        },
      }}
    >
      {!hideBreadcrumbs ? (
        <Box>
          <Group
            h={breadcrumbsHeight}
            align="center"
            position={isMobile ? 'apart' : 'left'}
            pos="relative"
            style={{ zIndex: 1 }}
            noWrap
          >
            <Box
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                overflowX: 'auto',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <Breadcrumbs>
                {breadcrumbItems.map(({ title, link }, index) => (
                  <Breadcrumb key={index} title={title} link={link} />
                ))}
              </Breadcrumbs>
            </Box>
            {mobileTopActions}
          </Group>
        </Box>
      ) : (
        mobileTopActions && (
          <Group h={breadcrumbsHeight} align="center" position="right" noWrap>
            {mobileTopActions}
          </Group>
        )
      )}
      {showTitleRow && (
        <Group mih={titleHeight} align="center" spacing={16} pt={isTablet ? 8 : 0} pb={8} position={groupPosition}>
          <Group maw={750} spacing={16}>
            <H2Medium color={titleColor}>{title}</H2Medium>
            {withToggle && (
              <Switch mt={6} onChange={(event) => onToggleChange(event.currentTarget.checked)} label={toggleLabel} />
            )}
          </Group>
          <Group spacing={16}>
            {withSearch && (
              <Input
                miw={336}
                rightSection={<IconSearch stroke={1.5} color={theme.colors.gray[6]} />}
                placeholder="Hľadať v tabuľke"
                onChange={(event) => onSearch(event.currentTarget.value)}
              />
            )}
            {isDiscarded && (
              <Flex bg="red.0" style={{ borderRadius: '40px', border: '1px solid white' }} px={16} py={8} gap={8}>
                <IconExclamationCircle size={24} color={theme.colors.red[9]} />
                <P1Medium color="red.9">Vyradené zariadenie</P1Medium>
              </Flex>
            )}
            {actions}
          </Group>
        </Group>
      )}
      <Box pos="relative" h={h}>
        {children}
      </Box>
    </AppShell>
  );
}

/**
 * Dashboard layout wrapper with provider.
 */
export default function DashboardLayout(props: DashboardLayoutProps) {
  return (
    <DashboardLayoutProvider>
      <DashboardLayoutImpl {...props} />
    </DashboardLayoutProvider>
  );
}
